import { useEffect, useRef, useState } from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

export interface SearchComboboxHit {
  id: string;
  name: string;
  /** Optional secondary line (e.g. customer email). */
  sublabel?: string | null;
}

export interface SearchComboboxProps {
  /** Currently selected id, or "" when nothing is selected. */
  value: string;
  /** Trigger label for a value not picked this session (prefill / edit). */
  selectedLabel?: string | null;
  onSelect: (id: string, hit: SearchComboboxHit) => void;
  /** Receives the trimmed query; called with "" on open when `fetchOnOpen` is set. */
  search: (query: string) => Promise<SearchComboboxHit[]>;
  /** On open with an empty query, call `search("")` for a default page (e.g. most recent). */
  fetchOnOpen?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  /** Initial query to seed the search box with when opened (e.g. a name pulled from a contract). */
  seedQuery?: string;
  createOption?: { label: string; onSelect: () => void };
  disabled?: boolean;
  className?: string;
  testId?: string;
}

export function SearchCombobox({
  value,
  selectedLabel,
  onSelect,
  search,
  fetchOnOpen,
  placeholder = "Search…",
  searchPlaceholder = "Type to search…",
  emptyText = "No results found",
  seedQuery,
  createOption,
  disabled,
  className,
  testId,
}: SearchComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(seedQuery ?? "");
  const [results, setResults] = useState<SearchComboboxHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [label, setLabel] = useState(selectedLabel ?? "");

  const debouncedQuery = useDebouncedValue(query, 400);
  const reqId = useRef(0);
  // Ref, not a dependency: callers pass a fresh closure each render, which would re-fire the fetch in a loop.
  const searchRef = useRef(search);
  searchRef.current = search;

  useEffect(() => {
    if (!value) {
      setLabel("");
    } else if (selectedLabel) {
      setLabel(selectedLabel);
    }
  }, [value, selectedLabel]);

  useEffect(() => {
    const q = debouncedQuery.trim();
    // While closed, clear so the next open starts fresh.
    if (!q && !(fetchOnOpen && open)) {
      setResults([]);
      setLoading(false);
      return;
    }
    const id = ++reqId.current;
    setLoading(true);
    searchRef.current(q)
      .then((hits) => {
        if (id === reqId.current) setResults(hits);
      })
      .catch(() => {
        if (id === reqId.current) setResults([]);
      })
      .finally(() => {
        if (id === reqId.current) setLoading(false);
      });
  }, [debouncedQuery, fetchOnOpen, open]);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Show the spinner immediately on open so the default-page fetch doesn't flash "no results".
        if (next && fetchOnOpen && !debouncedQuery.trim()) setLoading(true);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "w-full min-w-0 justify-between font-normal",
            !label && "text-muted-foreground",
            className,
          )}
        >
          <span className="min-w-0 flex-1 truncate text-left">
            {label || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {loading && (
              <div className="flex items-center justify-center py-6">
                <Spinner className="size-4 text-muted-foreground" />
              </div>
            )}
            {!loading && results.length === 0 && (query.trim() !== "" || fetchOnOpen) && (
              <CommandEmpty>{emptyText}</CommandEmpty>
            )}
            {!loading && results.length > 0 && (
              <CommandGroup>
                {results.map((hit) => (
                  <CommandItem
                    key={hit.id}
                    value={hit.id}
                    onSelect={() => {
                      setLabel(hit.name);
                      onSelect(hit.id, hit);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 size-4",
                        value === hit.id ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate">{hit.name}</span>
                      {hit.sublabel && (
                        <span className="truncate text-xs text-muted-foreground">
                          {hit.sublabel}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {createOption && (
              <CommandGroup>
                <CommandItem
                  value="__create__"
                  onSelect={() => {
                    createOption.onSelect();
                    setOpen(false);
                  }}
                  className="text-primary font-medium"
                >
                  <Plus className="mr-2 size-4" />
                  {createOption.label}
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
